import { useRef, useState } from "react";
import { Upload, X, Star, ImagePlus } from "lucide-react";
import api from "../lib/api";

/**
 * PhotoUpload — manages a list of uploaded photos for a business.
 * Calls /api/upload (generic upload) then exposes a list of { id, url, is_cover }.
 * Used during business registration (no business_id yet) so it just stores files;
 * after the business is created, the parent will associate cover_image_url with one
 * of these photos and attach the rest via /api/businesses/{id}/photos.
 */
export default function PhotoUpload({ photos, onChange, max = 6 }) {
  const fileInput = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const triggerPick = () => fileInput.current?.click();

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr("");
    setUploading(true);
    const next = [...photos];
    try {
      for (const f of files) {
        if (next.length >= max) break;
        if (!f.type.startsWith("image/")) continue;
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push({ id: data.id, url: data.url, is_cover: next.length === 0 });
      }
      onChange(next);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removeAt = (idx) => {
    const next = photos.filter((_, i) => i !== idx);
    if (next.length && !next.some((p) => p.is_cover)) next[0].is_cover = true;
    onChange(next);
  };

  const setCover = (idx) => {
    onChange(photos.map((p, i) => ({ ...p, is_cover: i === idx })));
  };

  return (
    <div data-testid="photo-upload">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((p, i) => (
          <div key={p.id} className="relative group rounded-xl overflow-hidden border border-stone-200 aspect-[4/3] bg-stone-100">
            <img src={p.url.startsWith("http") ? p.url : `${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="w-full h-full object-cover" />
            {p.is_cover && (
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-lokaso-gold text-lokaso-dark text-xs font-bold">
                <Star className="w-3 h-3" fill="currentColor" /> Couverture
              </span>
            )}
            <div className="absolute inset-0 flex items-end justify-between p-2 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition">
              {!p.is_cover && (
                <button type="button" onClick={() => setCover(i)} className="text-xs px-2 py-1 rounded-md bg-white text-lokaso-dark font-semibold" data-testid={`photo-set-cover-${i}`}>
                  Définir couverture
                </button>
              )}
              <button type="button" onClick={() => removeAt(i)} className="ml-auto p-1.5 rounded-md bg-white text-lokaso-red" data-testid={`photo-remove-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}

        {photos.length < max && (
          <button
            type="button"
            onClick={triggerPick}
            disabled={uploading}
            className="rounded-xl border-2 border-dashed border-stone-300 hover:border-lokaso-green hover:bg-lokaso-green/5 aspect-[4/3] flex flex-col items-center justify-center gap-2 text-stone-500"
            data-testid="photo-add-btn"
          >
            {uploading ? (
              <span className="text-sm">Envoi…</span>
            ) : (
              <>
                <ImagePlus className="w-7 h-7" />
                <span className="text-sm font-medium">Ajouter une photo</span>
                <span className="text-xs text-stone-400">{photos.length}/{max}</span>
              </>
            )}
          </button>
        )}
      </div>
      <input ref={fileInput} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="photo-file-input" />
      {err && <p className="mt-2 text-sm text-lokaso-red">{err}</p>}
      <p className="mt-3 text-xs text-stone-500">JPG, PNG ou WebP. Maximum {max} photos. La première sera utilisée comme image de couverture par défaut.</p>
    </div>
  );
}
